import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, ConfirmDialog, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtMontant } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Loader2, Search, FileDown, Printer } from "lucide-react";

const ALL = "__all__";
const EMPTY = {
  code: "", redevable: "", acte_generateur_id: "", province: "", ville: "",
  commune: "", quartier: "", avenue: "", numero: "", telephone: "", rccm: "", numero_impot: "", email: "",
};

export default function Assujettis() {
  const { canWrite } = useAuth();
  const [rows, setRows] = useState([]);
  const [actes, setActes] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [villes, setVilles] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [filters, setFilters] = useState({ q: "", province: ALL, acte_generateur_id: ALL, statut: "all" });

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.province !== ALL) params.province = filters.province;
    if (filters.acte_generateur_id !== ALL) params.acte_generateur_id = filters.acte_generateur_id;
    if (filters.statut === "impayes") params.impayes = true;
    const { data } = await api.get("/assujettis", { params });
    setRows(data);
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/actes").then((r) => setActes(r.data));
    api.get("/geo/provinces").then((r) => setProvinces(r.data));
  }, []);

  // cascading in form
  useEffect(() => {
    if (form.province) api.get("/geo/villes", { params: { province: form.province } }).then((r) => setVilles(r.data));
    else setVilles([]);
  }, [form.province]);
  useEffect(() => {
    if (form.province && form.ville)
      api.get("/geo/communes", { params: { province: form.province, ville: form.ville } }).then((r) => setCommunes(r.data));
    else setCommunes([]);
  }, [form.province, form.ville]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setSheet(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ ...EMPTY, ...a });
    setSheet(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setProvince = (v) => setForm((f) => ({ ...f, province: v, ville: "", commune: "" }));
  const setVille = (v) => setForm((f) => ({ ...f, ville: v, commune: "" }));

  const save = async () => {
    if (!form.redevable.trim()) { toast.error("Le redevable est obligatoire"); return; }
    if (!form.acte_generateur_id) { toast.error("Sélectionnez un acte générateur"); return; }
    if (!form.province || !form.ville || !form.commune) { toast.error("Province, ville et commune sont obligatoires"); return; }
    const payload = { ...form };
    if (!payload.code) delete payload.code;
    try {
      if (editing) await api.put(`/assujettis/${editing.id}`, payload);
      else await api.post("/assujettis", payload);
      toast.success(editing ? "Assujetti modifié" : "Assujetti enregistré");
      setSheet(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/assujettis/${confirm.id}`);
      toast.success("Assujetti supprimé");
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setConfirm(null);
    }
  };

  const exportExcel = () => {
    const params = new URLSearchParams();
    if (filters.province !== ALL) params.set("province", filters.province);
    if (filters.acte_generateur_id !== ALL) params.set("acte_generateur_id", filters.acte_generateur_id);
    window.open(`${API}/assujettis/export/excel?${params.toString()}`, "_blank");
  };

  return (
    <div data-testid="assujettis-page">
      <PageHeader title="Gestion des Assujettis" subtitle="Enregistrement et suivi des redevables">
        <Button variant="outline" onClick={() => window.print()} data-testid="print-assujettis-btn">
          <Printer className="mr-1.5 h-4 w-4" /> Imprimer
        </Button>
        <Button variant="outline" onClick={exportExcel} data-testid="export-excel-btn">
          <FileDown className="mr-1.5 h-4 w-4" /> Export Excel
        </Button>
        {canWrite && (
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="add-assujetti-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Nouvel assujetti
          </Button>
        )}
      </PageHeader>

      {/* Filters */}
      <Card className="mb-4 border-slate-200 p-4 shadow-sm no-print">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Rechercher (code, redevable)…"
              value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              data-testid="assujetti-search" />
          </div>
          <Select value={filters.province} onValueChange={(v) => setFilters({ ...filters, province: v })}>
            <SelectTrigger data-testid="filter-province"><SelectValue placeholder="Province" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes les provinces</SelectItem>
              {provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.acte_generateur_id} onValueChange={(v) => setFilters({ ...filters, acte_generateur_id: v })}>
            <SelectTrigger data-testid="filter-acte"><SelectValue placeholder="Acte générateur" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous les actes</SelectItem>
              {actes.map((a) => <SelectItem key={a.id} value={a.id}>{a.nom}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.statut} onValueChange={(v) => setFilters({ ...filters, statut: v })}>
            <SelectTrigger data-testid="filter-statut"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="impayes">Impayés uniquement</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Users} title="Aucun assujetti" hint="Enregistrez votre premier redevable." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Code</TableHead>
                  <TableHead>Redevable</TableHead>
                  <TableHead>Acte Générateur</TableHead>
                  <TableHead>Province</TableHead>
                  <TableHead>Ville</TableHead>
                  <TableHead>Commune</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead className="text-right">Solde impayé</TableHead>
                  {canWrite && <TableHead className="w-24 text-right no-print">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a.id} className="hover:bg-slate-50" data-testid="assujetti-row">
                    <TableCell className="font-mono text-xs font-semibold text-slate-700">{a.code}</TableCell>
                    <TableCell className="font-medium text-slate-800">{a.redevable}</TableCell>
                    <TableCell className="text-slate-600">{a.acte_generateur_nom}</TableCell>
                    <TableCell className="text-slate-600">{a.province}</TableCell>
                    <TableCell className="text-slate-600">{a.ville}</TableCell>
                    <TableCell className="text-slate-600">{a.commune}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{a.telephone || "—"}</TableCell>
                    <TableCell className="text-right" data-testid="assujetti-solde">
                      {(() => {
                        const dus = (a.soldes || []).filter((s) => s.solde > 0.009);
                        if (dus.length === 0) return <span className="text-xs text-emerald-600">À jour</span>;
                        return (
                          <div className="flex flex-col items-end gap-0.5">
                            {dus.map((s) => (
                              <span key={s.monnaie} className="font-mono text-xs font-semibold text-red-600">
                                {fmtMontant(s.solde, s.monnaie)}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </TableCell>
                    {canWrite && (
                      <TableCell className="text-right no-print">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid="edit-assujetti-btn">
                          <Pencil className="h-4 w-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setConfirm(a)} data-testid="delete-assujetti-btn">
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Form sheet */}
      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="assujetti-sheet">
          <SheetHeader>
            <SheetTitle className="font-heading">{editing ? "Modifier l'assujetti" : "Nouvel assujetti"}</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Code Assujetti</Label>
                <Input value={form.code} onChange={(e) => setField("code", e.target.value)}
                  placeholder="Auto (ASS-00001)" disabled={!!editing} data-testid="field-code" />
              </div>
              <div className="space-y-1.5">
                <Label>Redevable *</Label>
                <Input value={form.redevable} onChange={(e) => setField("redevable", e.target.value)}
                  placeholder="Nom et raison sociale" data-testid="field-redevable" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Acte Générateur *</Label>
              <Select value={form.acte_generateur_id} onValueChange={(v) => setField("acte_generateur_id", v)}>
                <SelectTrigger data-testid="field-acte"><SelectValue placeholder="Sélectionner un acte" /></SelectTrigger>
                <SelectContent>
                  {actes.map((a) => <SelectItem key={a.id} value={a.id}>{a.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-md border-l-2 border-amber-500 bg-slate-50/60 p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Localisation</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Province *</Label>
                  <Select value={form.province} onValueChange={setProvince}>
                    <SelectTrigger data-testid="field-province"><SelectValue placeholder="Province" /></SelectTrigger>
                    <SelectContent>
                      {provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Ville *</Label>
                  <Select value={form.ville} onValueChange={setVille} disabled={!form.province}>
                    <SelectTrigger data-testid="field-ville"><SelectValue placeholder="Ville" /></SelectTrigger>
                    <SelectContent>
                      {villes.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Commune *</Label>
                  <Select value={form.commune} onValueChange={(v) => setField("commune", v)} disabled={!form.ville}>
                    <SelectTrigger data-testid="field-commune"><SelectValue placeholder="Commune" /></SelectTrigger>
                    <SelectContent>
                      {communes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Quartier</Label>
                  <Input value={form.quartier} onChange={(e) => setField("quartier", e.target.value)} data-testid="field-quartier" />
                </div>
                <div className="space-y-1.5">
                  <Label>Avenue</Label>
                  <Input value={form.avenue} onChange={(e) => setField("avenue", e.target.value)} data-testid="field-avenue" />
                </div>
                <div className="space-y-1.5">
                  <Label>N°</Label>
                  <Input value={form.numero} onChange={(e) => setField("numero", e.target.value)} data-testid="field-numero" />
                </div>
              </div>
            </div>

            <div className="rounded-md border-l-2 border-slate-300 bg-slate-50/60 p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Identité fiscale & contact</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Téléphone</Label>
                  <Input value={form.telephone} onChange={(e) => setField("telephone", e.target.value)} data-testid="field-telephone" />
                </div>
                <div className="space-y-1.5">
                  <Label>RCCM</Label>
                  <Input value={form.rccm} onChange={(e) => setField("rccm", e.target.value)} data-testid="field-rccm" />
                </div>
                <div className="space-y-1.5">
                  <Label>N° Impôt</Label>
                  <Input value={form.numero_impot} onChange={(e) => setField("numero_impot", e.target.value)} data-testid="field-impot" />
                </div>
                <div className="space-y-1.5 sm:col-span-3">
                  <Label>Email (pour les relances de paiement)</Label>
                  <Input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)}
                    placeholder="redevable@exemple.cd" data-testid="field-email" />
                </div>
              </div>
            </div>
          </div>
          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setSheet(false)}>Annuler</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="save-assujetti-btn">Enregistrer</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Supprimer l'assujetti ?"
        description={`« ${confirm?.redevable} » sera définitivement supprimé.`}
        onConfirm={remove}
      />
    </div>
  );
}
